import React, { use, useEffect, useState } from "react";
import {
    Form,
    Input,
    InputNumber,
    Button,
    Select,
    Upload,
    Image,
    Space,
    Badge,
    Card,
    Tooltip
} from "antd";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeftOutlined, PlusOutlined, CheckCircleFilled, QuestionCircleOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { notify } from "../Customs/NotifyProvider";
import { RoomServices } from "../../Services/RoomServices";
import { GeneralService } from "../../Services/GeneralService";
import { formatDate } from "../Customs/dateFormatter";
import { ENDPOINTS } from "../../Services/ENDPOINT";

const { Option } = Select;

/* ─── inline styles ─────────────────────────────────────────────────────── */
const S = {
    page: {
        minHeight: "100vh",
        background: "#f1f2f4",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "0 0 60px",
    },
    topBar: {
        // display: "flex",
        // alignItems: "center",
        // gap: 12,
        position: "sticky",
        top: 100,
        zIndex: 100,
    },
    backBtn: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "none",
        border: "1px solid #c9cccf",
        borderRadius: 8,
        padding: "6px 12px",
        cursor: "pointer",
        fontSize: 13,
        color: "#202223",
        fontWeight: 500,
        transition: "background .15s",
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: "#202223",
        margin: 0,
    },
    body: {
        maxWidth: 1100,
        margin: "0 auto",
        padding: "24px 20px",
        display: "grid",
        gridTemplateColumns: "1fr 300px",
        gap: 20,
        alignItems: "start",
    },
    leftCol: { display: "flex", flexDirection: "column", gap: 16 },
    rightCol: { display: "flex", flexDirection: "column", gap: 16 },

    card: {
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e1e3e5",
        overflow: "hidden",
    },
    cardHeader: {
        padding: "14px 20px",
        borderBottom: "1px solid #e1e3e5",
        fontSize: 14,
        fontWeight: 650,
        color: "#202223",
        letterSpacing: "-0.01em",
    },
    cardBody: { padding: "20px" },

    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" },
    grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" },

    label: {
        display: "block",
        fontSize: 13,
        fontWeight: 500,
        color: "#202223",
        marginBottom: 4,
    },
    sublabel: { fontSize: 12, color: "#6d7175", marginBottom: 12 },

    saveBar: {
        background: "#fff",
        borderTop: "1px solid #e1e3e5",
        padding: "14px 24px",
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
    },
    btnPrimary: {
        background: "#008060",
        border: "none",
        borderRadius: 8,
        color: "#fff",
        fontWeight: 600,
        fontSize: 14,
        height: 38,
        padding: "0 20px",
        cursor: "pointer",
    },
    btnSecondary: {
        background: "#fff",
        border: "1px solid #c9cccf",
        borderRadius: 8,
        color: "#202223",
        fontWeight: 500,
        fontSize: 14,
        height: 38,
        padding: "0 20px",
        cursor: "pointer",
    },

    mediaGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, 100px)",
        gap: 10,
        marginTop: 12,
    },
    mediaThumb: (selected) => ({
        border: selected ? "2px solid #008060" : "1px solid #e1e3e5",
        borderRadius: 8,
        padding: 3,
        cursor: "pointer",
        position: "relative",
        transition: "border-color .15s",
    }),
    checkIcon: {
        position: "absolute",
        top: 4,
        right: 4,
        color: "#008060",
        fontSize: 16,
        background: "#fff",
        borderRadius: "50%",
        lineHeight: 1,
    },

    statusDot: (color) => ({
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
        marginRight: 6,
    }),
    sideSection: { padding: "14px 16px", borderBottom: "1px solid #e1e3e5" },
    sideSectionLast: { padding: "14px 16px" },
    sideLabel: { fontSize: 12, color: "#6d7175", marginBottom: 6, fontWeight: 500 },
    sideValue: { fontSize: 14, color: "#202223", fontWeight: 500 },
};

/* ─── component ─────────────────────────────────────────────────────────── */
const AddRoom = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const status = Form.useWatch("status", form);
    const additionalOccupancy = Form.useWatch("additional_occupancy", form);
    const roomName = Form.useWatch("room_name", form);
    const roomNumber = Form.useWatch("room_number", form);
    const pricePerDay = Form.useWatch("price_per_day", form);
    const currentStatus = Form.useWatch("status", form) || "available";

    const isDisabled = status && !["available", "maintenance"].includes(status);
    const isMaxDisabled = !(additionalOccupancy >= 1);

    const roomData = location.state?.room;
    const isEdit = !!roomData;

    const [fileList, setFileList] = useState([]);
    const [description, setDescription] = useState("");
    const [ammOptions, setAmmOptions] = useState([]);
    const [mediaLibrary, setMediaLibrary] = useState([]);
    const [mediaTab, setMediaTab] = useState("upload"); // "upload" | "library"
    const [selRoomData, setSelRoomData] = useState(null);
    const [roomType, setRoomType] = useState([])
    const isSelected = (img) => fileList.some((f) => f.url?.includes(img.url));

    const statusColor = { available: "#008060", maintenance: "#ffc453", occupied: "#d72c0d", reserved: "#5c6ac4" };
    const statusLabel = { available: "Available", maintenance: "Maintenance", occupied: "Occupied", reserved: "Reserved" };

    const getRoomById = async (id) => {

        try {
            const req = await GeneralService.get("room_types");
            setRoomType(
                req.data.map((r) => ({
                    label: r.type_name,
                    value: r.id,
                }))
            );

            // console.log("req.data", req.data);

        } catch (error) {
            console.error("Error fetching room details:", error);
        }
    }
    /* init form */
    useEffect(() => {
        const loadRoomData = async () => {
            try {
                if (isEdit && roomData?.id) {
                    // Fetch latest room details
                    const res = await GeneralService.getById("room", roomData.id);

                    // console.log("Room Details:", res);

                    const data = res?.data || roomData;

                    let amenitiesParsed = [];
                    if (data.amenities) {
                        try {
                            amenitiesParsed =
                                typeof data.amenities === "string"
                                    ? JSON.parse(data.amenities)
                                    : data.amenities;
                        } catch (error) {
                            amenitiesParsed = [];
                        }
                    }

                    const cleanValue = (value, defaultValue = undefined) => {
                        return value === null ||
                            value === undefined ||
                            value === "null" ||
                            value === "undefined"
                            ? defaultValue
                            : value;
                    };

                    form.setFieldsValue({
                        room_name: cleanValue(data.room_name, ""),
                        room_number: cleanValue(data.room_number, ""),
                        room_type: cleanValue(data.room_type)
                            ? Number(data.room_type)
                            : undefined,
                        floor: cleanValue(data.floor, ""),

                        ac_charge: cleanValue(data.ac_charge, 0),
                        price_per_day: cleanValue(data.price_per_day, 0),
                        online_price: cleanValue(data.online_price, 0),
                        price_per_hour: cleanValue(data.price_per_hour, 0),
                        additional_24_hour: cleanValue(data.additional_24_hour, 0),

                        max_occupancy: cleanValue(data.max_occupancy),
                        add_cost_per_occupancy: cleanValue(data.add_cost_per_occupancy, 0),
                        additional_occupancy: cleanValue(data.additional_occupancy, 0),

                        review_count: cleanValue(data.review_count)
                            ? Number(data.review_count)
                            : 0,

                        review_rating: cleanValue(data.review_rating)
                            ? Number(data.review_rating)
                            : 4.9,

                        discount: cleanValue(data.discount)
                            ? Number(data.discount)
                            : 0,

                        status: cleanValue(data.status),

                        amenities: amenitiesParsed,

                        child_cost_per_person: cleanValue(data.child_cost_per_person),
                        child_age_limit: cleanValue(data.child_age_limit),
                    });

                    setDescription(data.description || "");

                    if (data.images) {
                        let imageArray = data.images;

                        if (typeof imageArray === "string") {
                            try {
                                imageArray = JSON.parse(imageArray);
                            } catch {
                                imageArray = imageArray.split(",");
                            }
                        }

                        setFileList(
                            imageArray.map((img, index) => {
                                let url = img;

                                if (
                                    url &&
                                    !url.startsWith("http") &&
                                    !url.startsWith("https")
                                ) {
                                    url = `${ENDPOINTS.cleanBase}${url}`;
                                }

                                return {
                                    uid: `${index}`,
                                    name: `room-${index}`,
                                    status: "done",
                                    url,
                                };
                            })
                        );
                    } else {
                        setFileList([]);
                    }
                } else {
                    form.resetFields();
                    setDescription("");
                    setFileList([]);
                }
            } catch (error) {
                console.error("Failed to load room data:", error);
            }
        };

        loadRoomData();
        getRoomById();
    }, [isEdit, roomData?.id, form]);

    useEffect(() => {
        (async () => {
            try {
                const res = await GeneralService.getAllAmenities();
                setAmmOptions(res.data.map((a) => ({ label: a.name, value: a.name })));
            } catch (e) { console.error(e); }
        })();
        (async () => {
            try {
                const res = await RoomServices.getAllUploadedImages();
                setMediaLibrary(res.data);
            } catch (e) { console.error(e); }
        })();
    }, []);

    const handleUploadChange = ({ fileList: fl }) => {
        if (fl.length <= 5) setFileList(fl);
    };

    const selectFromLibrary = (img) => {
        if (fileList.length >= 5) return notify.error("Error", "Maximum 5 images allowed");
        if (fileList.some((f) => f.url?.includes(img.url))) return notify.warning("Warning", "Image already selected");
        setFileList((prev) => [...prev, {
            uid: img.url + Date.now(),
            name: "library-image",
            status: "done",
            url: `${ENDPOINTS.cleanBase}${img.url}`,
            isFromLibrary: true,
        }]);
        notify.success("Success", "Image added");
    };

    const onFinish = async (values) => {
        try {
            const formData = new FormData();
            const skipKeys = ["amenities", "description", "review_rating", "review_count", "discount"];
            Object.keys(values).forEach((key) => { if (!skipKeys.includes(key)) formData.append(key, values[key]); });
            formData.append("description", description);
            formData.append("review_rating", Number(values.review_rating));
            formData.append("review_count", Number(values.review_count));
            formData.append("discount", Number(values.discount));
            formData.append("amenities", JSON.stringify(values.amenities || []));
            formData.append("added_on", formatDate());
            formData.append("updated_on", formatDate());

            const existingImagePaths = [];
            fileList.forEach((file) => {
                if (file.originFileObj) {
                    formData.append("newImages", file.originFileObj);
                } else {
                    let cleanUrl = file.url || "";
                    if (cleanUrl.startsWith(ENDPOINTS.cleanBase)) cleanUrl = cleanUrl.replace(ENDPOINTS.cleanBase, "");
                    else if (cleanUrl.includes("localhost:3000")) cleanUrl = cleanUrl.replace("http://localhost:3000", "");
                    existingImagePaths.push(cleanUrl);
                }
            });
            formData.append("existingImages", JSON.stringify(existingImagePaths));

            const res = isEdit && roomData?.id
                ? await RoomServices.editRoom(roomData.id, formData)
                : await RoomServices.createRoom(formData);

            if (res?.status) notify.success("Success", res.message);
            else notify.error("Error", res.message);
        } catch (err) {

            console.error(err);
            notify.error("Error", "Something went wrong");
        }
    };

    const handleClear = () => {
        form.resetFields();
        setDescription("");
        setFileList([]);

        // Optional defaults after clear
        form.setFieldsValue({
            status: "available",
            review_rating: 4.9,
        });
    };

    /* ── render ─────────────────────────────────────────────────────────── */
    return (
        <Card
            title={
                <Space>
                    <ArrowLeftOutlined style={{ fontSize: 18 }} onClick={() => navigate(-1)} />
                    <h1 style={S.title}>{isEdit ? "Edit Room" : "Add Room"}</h1>
                </Space>
            }
            extra={
                <Button onClick={() => {
                    // console.log("roomData", roomData);

                    navigate('/room/history', { state: { room: roomData } })

                }
                }>History</Button>
            }
        >
            {/* Top bar */}

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <div style={S.body}>

                    {/* ── LEFT COLUMN ───────────────────────────────────── */}
                    <div style={S.leftCol}>

                        {/* Basic Details */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Basic Details</div>
                            <div style={S.cardBody}>
                                <div style={S.grid2}>
                                    <Form.Item label="Room Name" name="room_name" rules={[{ required: true }]}>
                                        <Input size="large" placeholder="e.g. Deluxe Suite" />
                                    </Form.Item>
                                    <Form.Item label="Room Number" name="room_number" rules={[{ required: true }]}>
                                        <Input size="large" placeholder="e.g. 101" />
                                    </Form.Item>
                                    <Form.Item label="Room Type" name="room_type">
                                        {/* <Input size="large" placeholder="e.g. Suite" /> */}
                                        <Select
                                            size="large"
                                            showSearch
                                            options={roomType}
                                            placeholder="Select Room Type"
                                            filterOption={(input, option) =>
                                                option?.label?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                    <Form.Item label="Floor" name="floor">
                                        <Input size="large" style={{ width: "100%" }} placeholder="1" />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Pricing</div>
                            <div style={S.cardBody}>
                                <div style={S.grid2}>
                                    <Form.Item label="Price Per Day (₹)" name="price_per_day">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                    <Form.Item label="Online Price (₹)" name="online_price">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                    <Form.Item label="Price Per Hour (₹)" name="price_per_hour">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                    <Form.Item label="Additional Price for 24 Hour (₹)" name="additional_24_hour">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                    <Form.Item label="Discount (%)" name="discount">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} max={100} />
                                    </Form.Item>

                                    <Form.Item
                                        label={
                                            <span>
                                                AC Charge (₹)&nbsp;
                                                <Tooltip title="This charge is added additionally if the user requires AC service">
                                                    <span style={{ cursor: "pointer", color: "#1890ff" }}><QuestionCircleOutlined style={{ font: 12 }} /></span>
                                                </Tooltip>
                                            </span>
                                        }
                                        name="ac_charge">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                        {/* Occupancy */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Occupancy</div>
                            <div style={S.cardBody}>
                                <div style={S.grid3}>
                                    <Form.Item label="Max Occupancy" name="max_occupancy">
                                        <InputNumber size="large" style={{ width: "100%" }} min={1} />
                                    </Form.Item>

                                    <Form.Item label="Extra Bed Slots" name="additional_occupancy">
                                        <InputNumber size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>

                                    <Form.Item label="Extra Cost / Person (₹)" name="add_cost_per_occupancy">
                                        <InputNumber disabled={isMaxDisabled} size="large" style={{ width: "100%" }} min={0} />
                                    </Form.Item>
                                    {/* child section */}
                                    {/* <Form.Item label="Max Child" name="max_child">
                                        <InputNumber size="large" style={{ width: "100%" }} min={1} />
                                    </Form.Item> */}

                                    <Form.Item
                                        label="Child Cost / Person (₹)"
                                        name="child_cost_per_person"
                                        getValueProps={(value) => ({
                                            value: value ?? undefined,
                                        })}
                                    >
                                        <InputNumber
                                            size="large"
                                            style={{ width: "100%" }}
                                            min={0}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Child Age Limit"
                                        name="child_age_limit"
                                        getValueProps={(value) => ({
                                            value: value ?? undefined,
                                        })}
                                    >
                                        <InputNumber
                                            disabled={isMaxDisabled}
                                            size="large"
                                            style={{ width: "100%" }}
                                            min={0}
                                        />
                                    </Form.Item>

                                </div>
                            </div>
                        </div>

                        {/* Description & Amenities */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Description</div>
                            <div style={S.cardBody}>
                                <ReactQuill
                                    value={description}
                                    onChange={setDescription}
                                    style={{ height: 180, marginBottom: 44 }}
                                />
                            </div>
                        </div>

                        <div style={S.card}>
                            <div style={S.cardHeader}>Amenities</div>
                            <div style={S.cardBody}>
                                <Form.Item name="amenities" style={{ marginBottom: 0 }}>
                                    <Select
                                        size="large"
                                        mode="tags"
                                        options={ammOptions}
                                        optionFilterProp="label"
                                        placeholder="Search or add amenities..."
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Reviews */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Reviews & Ratings</div>
                            <div style={S.cardBody}>
                                <div style={S.grid3}>
                                    <Form.Item label="Review Count" name="review_count">
                                        <InputNumber style={{ width: "100%" }} size="large" min={0} />
                                    </Form.Item>
                                    <Form.Item
                                        label="Review Rating"
                                        name="review_rating"
                                        initialValue={4.9}
                                        rules={[
                                            { required: true },
                                            { type: "number", min: 1, max: 5, message: "Must be 1–5" },
                                        ]}
                                    >
                                        <InputNumber size="large" min={1} max={5} step={0.1} style={{ width: "100%" }} />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                        {/* Images */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Room Images</div>
                            <div style={S.cardBody}>
                                {/* Mini toggle */}
                                <div style={{ display: "flex", gap: 0, marginBottom: 16, border: "1px solid #e1e3e5", borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
                                    {["upload", "library"].map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setMediaTab(t)}
                                            style={{
                                                padding: "7px 18px",
                                                fontSize: 13,
                                                fontWeight: 500,
                                                border: "none",
                                                cursor: "pointer",
                                                background: mediaTab === t ? "#008060" : "#fff",
                                                color: mediaTab === t ? "#fff" : "#202223",
                                                transition: "all .15s",
                                            }}
                                        >
                                            {t === "upload" ? "Upload" : "Media Library"}
                                        </button>
                                    ))}
                                </div>

                                {mediaTab === "upload" ? (
                                    <Upload
                                        listType="picture-card"
                                        fileList={fileList}
                                        beforeUpload={() => false}
                                        onChange={handleUploadChange}
                                        onRemove={(file) => {
                                            let cleanUrl = file.url || "";
                                            if (cleanUrl.startsWith(ENDPOINTS.cleanBase)) cleanUrl = cleanUrl.replace(ENDPOINTS.cleanBase, "");
                                            // console.log("Deleted Image Path:", cleanUrl);
                                            return true;
                                        }}
                                    >
                                        {fileList.length < 5 && (
                                            <div>
                                                <PlusOutlined />
                                                <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                                            </div>
                                        )}
                                    </Upload>
                                ) : (
                                    <div style={S.mediaGrid}>
                                        {mediaLibrary.map((img) => {
                                            const selected = isSelected(img);
                                            return (
                                                <div key={img.url} style={S.mediaThumb(selected)} onClick={() => selectFromLibrary(img)}>
                                                    <Image
                                                        src={`${ENDPOINTS.cleanBase}${img.url}`}
                                                        preview={false}
                                                        width={94}
                                                        height={94}
                                                        style={{ objectFit: "cover", borderRadius: 5, opacity: selected ? 0.7 : 1, display: "block" }}
                                                    />
                                                    {selected && <CheckCircleFilled style={S.checkIcon} />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <p style={{ fontSize: 12, color: "#6d7175", marginTop: 10 }}>
                                    {fileList.length}/5 images selected
                                </p>
                            </div>
                        </div>

                    </div>

                    {/* ── RIGHT COLUMN ──────────────────────────────────── */}
                    <div style={{ ...S.rightCol, ...S.topBar }}>

                        {/* Status card */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Status</div>
                            <div style={{ padding: "16px" }}>
                                <Form.Item name="status" initialValue="available" style={{ marginBottom: 0 }}>
                                    <Select size="large" style={{ width: "100%" }} disabled={isDisabled}>
                                        <Option value="available">
                                            <span style={S.statusDot("#008060")} />Available
                                        </Option>
                                        <Option value="maintenance">
                                            <span style={S.statusDot("#ffc453")} />Maintenance
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </div>
                        </div>

                        {/* Summary card */}
                        <div style={S.card}>
                            <div style={S.cardHeader}>Summary</div>
                            <div style={S.sideSection}>
                                <div style={S.sideLabel}>Room</div>
                                <div style={S.sideValue}>{roomName || "—"}</div>
                                {roomNumber && <div style={{ fontSize: 12, color: "#6d7175", marginTop: 2 }}>No. {roomNumber}</div>}
                            </div>
                            <div style={S.sideSection}>
                                <div style={S.sideLabel}>Status</div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={S.statusDot(statusColor[currentStatus] || "#6d7175")} />
                                    <span style={S.sideValue}>{statusLabel[currentStatus] || "—"}</span>
                                </div>
                            </div>
                            <div style={S.sideSection}>
                                <div style={S.sideLabel}>Price / Day</div>
                                <div style={S.sideValue}>{pricePerDay ? `₹${pricePerDay}` : "—"}</div>
                            </div>
                            <div style={S.sideSectionLast}>
                                <div style={S.sideLabel}>Images</div>
                                <div style={S.sideValue}>{fileList.length} / 5</div>
                            </div>
                        </div>

                        {/* Actions */}
                        {/* <div style={S.card}>
                            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                                <Button
                                    size="large"
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    style={{ background: "#008060", borderColor: "#008060", fontWeight: 600, borderRadius: 8 }}
                                >
                                    {isEdit ? "Update Room" : "Save Room"}
                                </Button>
                                <Button
                                    size="large"
                                    block
                                    style={{ borderRadius: 8 }}
                                    onClick={() => navigate("/manage-rooms")}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div> */}

                        {/* Actions */}
                        <div style={S.card}>
                            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>

                                <Button
                                    size="large"
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    style={{
                                        background: "#008060",
                                        borderColor: "#008060",
                                        fontWeight: 600,
                                        borderRadius: 8
                                    }}
                                >
                                    {isEdit ? "Save Changes" : "Save Room"}
                                </Button>

                                <Button
                                    size="large"
                                    block
                                    danger
                                    style={{ borderRadius: 8 }}
                                    onClick={handleClear}
                                >
                                    Clear
                                </Button>

                                <Button
                                    size="large"
                                    block
                                    style={{ borderRadius: 8 }}
                                    onClick={() => navigate("/manage-rooms")}
                                >
                                    Cancel
                                </Button>

                            </div>
                        </div>

                    </div>
                </div>
            </Form>
        </Card>
    );
};

export default AddRoom;